import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getFormValues, reduxForm } from 'redux-form';
import moment from 'moment';
import momentLocalizer from 'react-widgets-moment';

import {
  UCT_FULL_FILE,
  VALID_EMAIL_REGEX,
  SUPER_ADMIN,
} from '../../config/app_config';
import { cancelOrder, createNewOrder, goToPage, listUseCases } from '../../actions/order';
import { getPriceBook, getRevShare } from '../../actions/pricing';
import { fetchDatasetsById, fetchFieldsetsById } from '../../actions/data';
import { closeDialog, openDialog } from '../../actions/dialog';
import { getAccountById } from '../../actions/superadmin';
import { addCommas, getOrderTotalCost, storeWithExpiration } from '../../utils/common';
import CreateNewOrderForm from './CreateNewOrderForm';

moment.locale('en');
momentLocalizer();

momentLocalizer(moment);

export class CreateNewOrderFormContainer extends React.Component {
  static propTypes = {
    account: PropTypes.object.isRequired,
    accountId: PropTypes.number.isRequired,
    accountSettings: PropTypes.object.isRequired,
    cancelOrderAction: PropTypes.func.isRequired,
    change: PropTypes.func.isRequired,
    closeDialogAction: PropTypes.func.isRequired,
    createOrderAction: PropTypes.func.isRequired,
    dataSets: PropTypes.object.isRequired,
    dialog: PropTypes.object.isRequired,
    dirty: PropTypes.bool.isRequired,
    fetchDatasetsByIdAction: PropTypes.func.isRequired,
    fetchFieldsetsByIdAction: PropTypes.func.isRequired,
    fieldSets: PropTypes.object.isRequired,
    formValues: PropTypes.object,
    getAccountByIdAction: PropTypes.func.isRequired,
    getPriceBookAction: PropTypes.func.isRequired,
    getRevShareAction: PropTypes.func.isRequired,
    goToPageAction: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    invalid: PropTypes.bool.isRequired,
    isSuperAdmin: PropTypes.bool.isRequired,
    listUseCasesAction: PropTypes.func.isRequired,
    openDialogAction: PropTypes.func.isRequired,
    page: PropTypes.string.isRequired,
    platformMap: PropTypes.object.isRequired,
    priceBook: PropTypes.object.isRequired,
    submitting: PropTypes.bool.isRequired,
    useCaseMap: PropTypes.object.isRequired,
  };

  static defaultProps = {
    formValues: undefined,
  };

  state = {
    checkedArray: [],
    removedCases: [],
  };

  componentWillMount = () => {
    const {
      accountId,
      fetchDatasetsByIdAction,
      listUseCasesAction,
      getAccountByIdAction,
    } = this.props;
    const token = storeWithExpiration.get('token');
    getAccountByIdAction(accountId, token);
    fetchDatasetsByIdAction(accountId, token);
    listUseCasesAction(accountId, token);
  };

  pad = number => {
    if (number < 10) {
      return '0'.concat(number);
    }
    return number;
  };

  clearUseCases = () => {
    this.setState({ checkedArray: [], removedCases: [] });
  };

  addToList = (name, checked, list) => {
    const { removedCases } = this.state;
    const location = list.indexOf(name);

    if (location >= 0) {
      if (!checked) {
        list.splice(location, 1);
        if (name === 'FULL_FILE_INSTALL') {
          removedCases.forEach(element => list.push(element));
          this.setState({ removedCases: [] });
        }
      }
    } else if (checked) {
      if (name === 'FULL_FILE_INSTALL') {
        const removed = list.splice(0);
        this.setState({ removedCases: removed });
      }
      list.push(name);
    }
    this.setState({ checkedArray: list });
  };

  escapeForm = (isFormDirty = true, evt = false) => {
    if (isFormDirty) {
      // eslint-disable-next-line
      if (window.confirm('You are about to lose your form data, are you sure you want to leave?')) {
        this.leaveAndResetForm(evt);
      }
    } else {
      this.leaveAndResetForm(evt);
    }
  };

  formatUseCases = useCaseArray => {
    const {
      formValues: { platformCode },
      useCaseMap,
    } = this.props;
    const platformUseCases = useCaseMap.get(platformCode);
    const descriptions = useCaseArray.map(useCase => {
      const uc = platformUseCases.find(puc => puc.code === useCase);
      return uc.description;
    });

    return descriptions.join(', ');
  };

  leaveAndResetForm = evt => {
    const { cancelOrderAction } = this.props;
    if (evt !== false) {
      evt.preventDefault();
    }

    cancelOrderAction();
  };

  formatDate = dateObject =>
    ''
      .concat(dateObject.getUTCFullYear())
      .concat('-')
      .concat(this.pad(dateObject.getUTCMonth() + 1))
      .concat('-')
      .concat(this.pad(dateObject.getUTCDate()));

  previousPage = () => {
    const {
      accountId,
      goToPageAction,
      isSuperAdmin,
      formValues: { useCaseType },
    } = this.props;
    const page = parseInt(this.props.page, 10);
    let actionParams;
    // If we're on page 5 and we aren't using the search frame, jump back to page 3
    if (page === 5 && useCaseType === UCT_FULL_FILE) {
      actionParams = isSuperAdmin ? [3, accountId] : [3];
    } else {
      actionParams = isSuperAdmin ? [page - 1, accountId] : [page - 1];
    }
    goToPageAction(...actionParams);
  };

  nextPage = (currPage = null) => {
    const {
      accountId,
      goToPageAction,
      isSuperAdmin,
      formValues: { useCaseType },
    } = this.props;
    const page = typeof currPage === 'number' ? currPage : parseInt(this.props.page, 10);
    let actionParams;
    // If we're on page 3 and we aren't using the search frame jump to page 5
    if (page === 3 && useCaseType === UCT_FULL_FILE) {
      actionParams = isSuperAdmin ? [5, accountId] : [5];
    } else {
      actionParams = isSuperAdmin ? [page + 1, accountId] : [page + 1];
    }
    goToPageAction(...actionParams);
  };

  performCreateOrder = params => {
    const { createOrderAction, openDialogAction, accountId, isSuperAdmin } = this.props;
    const { checkedArray } = this.state;
    const token = storeWithExpiration.get('token');

    const dataset = JSON.parse(params.listId);
    const fieldset = JSON.parse(params.skuId);

    const companyInput = {
      id: null,
      name: params.companyName,
      alias: '',
      code: null,
      addressLine1: params.addressLine1,
      addressLine2: params.addressLine2,
      city: params.city,
      state: params.state,
      country: params.country,
      zipCode: params.zipCode,
      phone: params.companyPhone,
    };

    const personInput = {
      id: null,
      title: '',
      firstName: params.firstName,
      lastName: params.lastName,
      email: params.email,
      phone: params.phone,
    };

    const platformUseCaseRequestsInput = checkedArray.map(useCase => ({
      platformCode: params.platformCode,
      useCaseCode: useCase,
    }));

    const listSKURequestsInput = [
      {
        listId: Number(dataset.id),
        listName: dataset.name,
        skuId: Number(fieldset.id),
        skuName: fieldset.name,
      },
    ];

    const searchQueryRequests = params.segmentBuilderFilters
      ? JSON.parse(params.segmentBuilderFilters)
      : null;

    // const pTotal = formatDollar(params.pricePerSeatPerMonth * params.numberOfSeats);
    // const pPSPM = params.pricePerSeatPerMonth ? params.pricePerSeatPerMonth : 0;
    const numSeats = params.numberOfSeats ? Number(params.numberOfSeats) : 0;

    const isBillable = params.isBillable === 'true';
    const billingInfo =
      params.billingInfo === 'Other' ? params.billingInfoOther : params.billingInfo;

    const orderRequest = {
      accountId,
      company: companyInput,
      person: personInput,
      providerOrderNumber: params.providerOrderNumber,
      providerCustomerNumber: params.providerCustomerNumber,
      autoRenew: params.autoRenew || false,
      numberOfSeats: numSeats,
      recordCredits: params.recordCredits ? Number(params.recordCredits) : null,
      cost: isSuperAdmin ? null : 0,
      currency: 'USD',
      isBillable,
      billingInfo: !isBillable ? billingInfo : null,
      matchedPairsProvided: params.matchedPairsProvided === 'true',
      contactMatchByAccount: params.contactMatchByAccount === 'true',
      csvDownloadEnabled: params.csvDownloadEnabled === 'true',
      leadMatchEnabled: params.leadMatchEnabled === 'true',
      useEndUserCredentials: params.useEndUserCredentials === 'true',
      platformUseCaseRequests: platformUseCaseRequestsInput,
      listSKURequests: listSKURequestsInput,
      startDate: `${this.formatDate(params.startDay)}${'T00:00:00'}`,
      endDate: `${this.formatDate(params.endDay)}${'T00:00:00'}`,
      customSettings: params.customSettings === undefined ? null : params.customSettings,
    };

    // If partial file, add searchQueryRequests
    if (searchQueryRequests !== null) {
      orderRequest.searchQueryRequests = searchQueryRequests;
    }

    createOrderAction(token, orderRequest, isSuperAdmin);
    if (params.matchedPairsProvided === 'true') {
      openDialogAction('redirectToPrematchedUploadDialog');
    }
  };

  renderCompanyInfo = () => {
    const {
      companyName,
      companyPhone,
      addressLine1,
      addressLine2,
      city,
      state,
      country,
      zipCode,
      providerCustomerNumber,
      providerOrderNumber,
    } = this.props.formValues;

    return (
      <div className="card-block order-company-info">
        {!companyName || companyName === undefined ? null : (
          <div>
            <h5>
              <strong>{companyName}</strong>
            </h5>
            <div className="row">
              <div className="col-md-6">
                <p>
                  {' '}
                  {companyPhone} <br />
                  {addressLine1} <br />
                  {addressLine2 === undefined ? null : (
                    <span>
                      {' '}
                      {addressLine2} <br />{' '}
                    </span>
                  )}
                  {city}, {state}&nbsp;{zipCode} <br />
                  {country}
                </p>
              </div>
              <div className="col-md-6">
                <p>
                  Customer # {providerCustomerNumber}
                  <br />
                  Order # {providerOrderNumber}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  renderOrderSummary = selectedPbData => {
    const {
      account,
      formValues: {
        companyName,
        companyPhone,
        addressLine1,
        addressLine2,
        billingInfo,
        billingInfoOther,
        isBillable,
        city,
        state,
        country,
        zipCode,
        firstName,
        lastName,
        email,
        phone,
        matchedPairsProvided,
        contactMatchByAccount,
        leadMatchEnabled,
        csvDownloadEnabled,
        useEndUserCredentials,
        providerCustomerNumber,
        providerOrderNumber,
        platformCode,
        startDay,
        endDay,
        useCaseType,
        numberOfSeats,
        recordCredits,
        listId,
        skuId,
      },
      platformMap,
    } = this.props;
    const { checkedArray } = this.state;
    const startDate = new Date(startDay);
    const endDate = new Date(endDay);
    const isCRMTable = platformCode === 'SFDC' || platformCode === 'DYNAMICS';
    const dataset = JSON.parse(listId);
    const fieldset = JSON.parse(skuId);
    const hasAddressLine2 = addressLine2 !== undefined && addressLine2 !== '';
    const creditsEnabled = account.get('creditsEnabled');

    return (
      <div>
        <p>
          <strong>Customer Details</strong>
        </p>
        <div className="row">
          <div className="col-md-6">
            <p>
              <i>{companyName}</i>
              <br />
              <i>{companyPhone}</i>
              <br />
              <i>{addressLine1}</i>
              <br />
              {hasAddressLine2 ? <i>{addressLine2}</i> : null}
              {hasAddressLine2 ? <br /> : null}
              <i>
                {city}, {state} {zipCode}
              </i>
              <br />
              <i>{country}</i>
            </p>
          </div>
          <div className="col-md-6">
            <p>
              Customer # <i>{providerCustomerNumber}</i>
              <br />
              Order # <i>{providerOrderNumber}</i>
            </p>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6">
            <p>
              <strong>Company Admin</strong>
            </p>
            <p>
              <i>
                {firstName} {lastName}
              </i>
              <br />
              <i>{email}</i>
              <br />
              <i>{phone}</i>
            </p>
          </div>
          <div className="col-md-6" />
        </div>
        <p>
          <strong>Order Details</strong>
        </p>
        <div className="row">
          <div className="col-md-6">
            <p>
              {' '}
              Platform:&nbsp;
              <i>
                {platformMap.get(platformCode) === undefined
                  ? null
                  : platformMap.get(platformCode).description}
              </i>
              <br />
              List Criteria: <i>{useCaseType === UCT_FULL_FILE ? 'Full File' : 'Partial File'}</i>
              <br />
              Use Case:&nbsp;
              <i>{this.formatUseCases(checkedArray)}</i>
              <br />
            </p>
            <p>
              {' '}
              Pre-matched Pairs: <i>{matchedPairsProvided === 'true' ? 'Yes' : 'No'}</i>
              <br />
              Contact Match Within Matched Account:{' '}
              <i>{contactMatchByAccount === 'true' ? 'Yes' : 'No'}</i>
              <br />
              Lead Match Enabled: <i>{leadMatchEnabled === 'true' ? 'Yes' : 'No'}</i>
              <br />
              CSV Download Enabled: <i>{csvDownloadEnabled === 'true' ? 'Yes' : 'No'}</i>
              <br />
              Use End User Credentials: <i>{useEndUserCredentials === 'true' ? 'Yes' : 'No'}</i>
              <br />
              List: <i>{dataset.name}</i>
              <br />
              SKU: <i>{fieldset.name}</i>
            </p>
          </div>
          <div className="col-md-6">
            <p>
              Billable:{' '}
              <i>
                {isBillable === 'true'
                  ? 'Yes'
                  : `No - ${billingInfo !== 'Other' ? billingInfo : billingInfoOther}`}
              </i>
              <br />
              {creditsEnabled ? (
                <span>
                  Number of Credits: <i>{addCommas(recordCredits)}</i>
                </span>
              ) : null}
              {creditsEnabled ? <br /> : null}
              {isCRMTable ? (
                <span>
                  Number of Seats: <i>{addCommas(numberOfSeats)}</i>
                  <br />
                </span>
              ) : null}
              <br />
              {isCRMTable ? (
                <span>
                  Amount owed Datarista per month:{' '}
                  <i>${getOrderTotalCost(numberOfSeats, selectedPbData, isCRMTable)}</i>
                </span>
              ) : null}
            </p>
            <p>
              {' '}
              Start Date: <i>{startDate.toDateString()}</i>
              <br />
              End Date: <i>{endDate.toDateString()}</i>
            </p>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { checkedArray } = this.state;
    const {
      account,
      accountId,
      accountSettings,
      cancelOrderAction,
      change,
      closeDialogAction,
      dataSets,
      dialog,
      dirty,
      fetchFieldsetsByIdAction,
      fieldSets,
      formValues,
      getPriceBookAction,
      getRevShareAction,
      handleSubmit,
      invalid,
      isSuperAdmin,
      openDialogAction,
      page,
      platformMap,
      priceBook,
      submitting,
      useCaseMap,
    } = this.props;

    return (
      <CreateNewOrderForm
        account={account}
        accountId={accountId}
        accountSettings={accountSettings}
        addToListFunction={this.addToList}
        cancelOrderFunction={cancelOrderAction}
        change={change}
        checkedArray={checkedArray}
        clearUseCasesFunction={this.clearUseCases}
        closeDialogFunction={closeDialogAction}
        companyName={formValues}
        createOrderFunction={this.performCreateOrder}
        creditsEnabled={account.get('creditsEnabled')}
        dataSets={dataSets}
        dialog={dialog}
        dirty={dirty}
        escapeForm={this.escapeForm}
        fetchFieldsetsByIdFunction={fetchFieldsetsByIdAction}
        fieldSets={fieldSets}
        formValues={formValues}
        getPriceBookFunction={getPriceBookAction}
        getRevShareFunction={getRevShareAction}
        handleSubmit={handleSubmit}
        invalid={invalid}
        isSuperAdmin={isSuperAdmin}
        nextPageFunction={this.nextPage}
        openDialogFunction={openDialogAction}
        page={page}
        platformMap={platformMap}
        previousPageFunction={this.previousPage}
        priceBook={priceBook}
        renderCompanyInfo={this.renderCompanyInfo}
        renderOrderSummaryChunk={this.renderOrderSummary}
        setSegmentBuilderFiltersFunction={this.setSegmentBuilderFilters}
        submitting={submitting}
        useCaseMap={useCaseMap}
      />
    );
  }
}

const validate = values => {
  const errors = {};
  const isCRM = values.platformCode === 'SFDC' || values.platformCode === 'DYNAMICS';
  const today = new Date(
    moment()
      .set('hour', 0)
      .set('minute', 0)
      .set('second', 0)
      .format()
  );

  /* ********** Page 1 ************* */
  if (!values.companyName || values.companyName.trim() === '') {
    errors.companyName = 'Required';
  }
  if (!values.providerCustomerNumber || values.providerCustomerNumber.trim() === '') {
    errors.providerCustomerNumber = 'Required';
  }
  if (!values.providerOrderNumber || values.providerOrderNumber.trim() === '') {
    errors.providerOrderNumber = 'Required';
  }

  /* ********** Page 2 ************* */
  if (!values.firstName || values.firstName.trim() === '') {
    errors.firstName = 'Required';
  }
  if (!values.lastName || values.lastName.trim() === '') {
    errors.lastName = 'Required';
  }
  if (!values.email || values.email.trim() === '') {
    errors.email = 'Required';
  } else if (!VALID_EMAIL_REGEX.test(values.email)) {
    errors.email = 'Invalid email address';
  }

  /* ********** Page 3 ************* */
  if (!values.listId || values.listId === '') {
    errors.listId = 'Required';
  }
  if (!values.skuId || values.skuId === '') {
    errors.skuId = 'Required';
  } else if (values.skuId === '-1') {
    errors.skuId = 'Please Select a List With a SKU and Try Again';
  }
  if (!values.platformCode || values.platformCode.trim() === '') {
    errors.platformCode = 'Required';
  }

  if (
    !values.BULK_APPEND &&
    !values.SINGLE_RECORD_APPEND &&
    !values.LIST_BUILDING &&
    !values.FULL_FILE_INSTALL &&
    !values.SB_BULK_APPEND &&
    !values.SB_SINGLE_RECORD_APPEND &&
    !values.SB_FULL_FILE_INSTALL
  ) {
    errors.BULK_APPEND = 'Required';
  }
  if (!values.matchedPairsProvided || values.matchedPairsProvided === '') {
    errors.matchedPairsProvided = 'Required';
  }

  if (!values.contactMatchByAccount || values.contactMatchByAccount === '') {
    errors.contactMatchByAccount = 'Required';
  }

  if (!values.csvDownloadEnabled || values.csvDownloadEnabled === '') {
    errors.csvDownloadEnabled = 'Required';
  }

  if (!values.leadMatchEnabled || values.leadMatchEnabled === '') {
    errors.leadMatchEnabled = 'Required';
  }

  if (!values.useEndUserCredentials || values.useEndUserCredentials === '') {
    errors.useEndUserCredentials = 'Required';
  }

  /* ********** Page 4 ************* */
  if (!values.segmentBuilderFilters) {
    errors.segmentBuilderFilters = 'Select filter criteria first';
  }

  /* ********** Page 5 ************* */
  // Credits
  if (values.creditsEnabled) {
    if (!values.recordCredits || values.recordCredits === '') {
      errors.recordCredits = 'Required';
    } else if (Number.isNaN(Number(values.recordCredits))) {
      errors.recordCredits = 'Must be a number';
    } else if (Number(values.recordCredits) < 0) {
      errors.recordCredits = 'Must be 0 or greater';
    } else if (parseInt(values.recordCredits, 10) !== Number(values.recordCredits)) {
      errors.recordCredits = 'Should be an integer';
    }
  }
  // Number of seats
  // Nested if statements seem to break validate function. Using workaround
  if (!isCRM) {
    // Don't validate Number of seats
  } else if (!values.numberOfSeats || values.numberOfSeats === '') {
    errors.numberOfSeats = 'Required';
  } else if (Number.isNaN(Number(values.numberOfSeats))) {
    errors.numberOfSeats = 'Must be a number';
  } else if (Number(values.numberOfSeats) < 1) {
    errors.numberOfSeats = 'Must be greater than 0';
  } else if (parseInt(values.numberOfSeats, 10) !== Number(values.numberOfSeats)) {
    errors.numberOfSeats = 'Should be an integer';
  } else if (parseInt(values.numberOfSeats, 10) > 1000) {
    errors.numberOfSeats = 'Please contact Datarista for orders over 1000';
  }
  // Start and end times
  if (values.endDay === null || values.endDay === undefined) {
    errors.endDay = 'Required';
  }
  if (values.startDay === null || values.startDay === undefined) {
    errors.startDay = 'Required';
  } else if (values.startDay < today) {
    errors.startDay = "Start date can't be before today";
  } else if (values.startDay >= values.endDay) {
    errors.startDay = 'Must preceed end date';
    errors.endDay = 'Must follow start date';
  }
  return errors;
};

const CreateNewOrderFormContainerRF = reduxForm({
  form: 'createOrder',
  destroyOnUnmount: false,
  validate,
})(CreateNewOrderFormContainer);

function mapStateToProps(state, ownProps) {
  const { account, accountSettings, data, dialog, orderList, user, priceBook, superadmin } = state;
  const { page, selectedAccountId } = ownProps.params;
  const isSuperAdmin = user.get('userTypeId') === SUPER_ADMIN;
  const formValues = getFormValues('createOrder')(state);
  const dataSets = data.get('dataSets');
  const fieldSets = data.get('fieldSets');
  const platformMap = orderList.get('platformMap');
  const useCaseMap = orderList.get('useCaseMap');
  const accountId = parseInt(selectedAccountId, 10) || account.id;
  const currAccount = isSuperAdmin ? superadmin.get('currAccount') : account;
  const today = new Date(
    moment()
      .set('hour', 0)
      .set('minute', 0)
      .set('second', 0)
      .format()
  );
  const oneYearFromNow = new Date(
    moment(today)
      .add(1, 'Y')
      .format()
  );
  const initialValues = {
    startDay: today,
    endDay: oneYearFromNow,
    isBillable: 'true',
    creditsEnabled: currAccount.get('creditsEnabled'),
    billingInfo: 'Sandbox',
    matchedPairsProvided: 'false',
    contactMatchByAccount: 'true',
    csvDownloadEnabled: 'false',
    leadMatchEnabled: 'false',
    useEndUserCredentials: 'false',
  };
  return {
    account: currAccount,
    accountSettings,
    dataSets,
    dialog,
    fieldSets,
    platformMap,
    useCaseMap,
    user,
    page,
    initialValues,
    formValues,
    priceBook,
    accountId,
    isSuperAdmin,
  };
}

export default connect(
  mapStateToProps,
  {
    cancelOrderAction: cancelOrder,
    closeDialogAction: closeDialog,
    createOrderAction: createNewOrder,
    fetchDatasetsByIdAction: fetchDatasetsById,
    fetchFieldsetsByIdAction: fetchFieldsetsById,
    goToPageAction: goToPage,
    listUseCasesAction: listUseCases,
    openDialogAction: openDialog,
    getAccountByIdAction: getAccountById,
    getRevShareAction: getRevShare,
    getPriceBookAction: getPriceBook,
  }
)(CreateNewOrderFormContainerRF);
